import CloseIcon from '@mui/icons-material/Close';
import { Autocomplete, Box, FormControl, Grid, IconButton, InputLabel, Select, Tab, Tabs, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import React from 'react';
import { Prompt, useParams } from 'react-router';

import { DataAPIContext, UPDATE } from '../api';
import AccessDeniedView from '../components/AccessDeniedView';
import AccessTagsDropdown from '../components/AccessTagsDropdown';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FormActionButtons from '../components/FormActionButtons';
import FormLicenseeContext, { toLicenseeId } from '../components/FormLicenseeContext';
import LicenseeFormField from '../components/LicenseeFormField';
import PageMeta from '../components/PageMeta';
import RecordDetails, { RecordDetailsContext } from '../components/RecordDetails';
import RevisionHistoryDialog from '../components/RevisionHistoryDialog';
import TabPanel, { a11yProps } from '../components/TabPanel';
import UsedByPanel from '../components/UsedByPanel';
import { ContextStore } from '../store/contextStore';
import { cardArrangementTypesMenuItems } from './utils';
import { ConfirmationDialogText } from './utils';

const useStyles = makeStyles((theme) => ({
    RegionEdit: {
        paddingTop: '1.5rem',
        paddingBottom: 0,
        marginBottom: 0,
        flex: 1,
        minHeight: 0,
        width: '100%',
        alignSelf: 'stretch',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxSizing: 'border-box',
    },
    tabPanelsHost: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        marginTop: theme.spacing(2),
    },
    tabPanelGeneral: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        scrollbarGutter: 'stable',
    },
    tabPanelHistory: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
    },
    detailsImage: {
        borderRadius: '20px',
        height: '250px',
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center',
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    imageLabel: {
        textTransform: 'uppercase',
        color: '#ffffff',
        fontWeight: 'bold',
        background: 'rgba(128,128,128,0.5)',
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
    },
    formFooterDocked: {
        flexShrink: 0,
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        paddingTop: 0,
        paddingBottom: 0,
        marginBottom: 0,
        backgroundColor: 'transparent',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexWrap: 'nowrap',
        boxSizing: 'border-box',
        width: '100%',
        [theme.breakpoints.down('sm')]: {
            paddingLeft: theme.spacing(0.5),
            paddingTop: theme.spacing(0.375),
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        },
    },
}));

const getComparableState = (region) => {
    const a = {
        ...region,
    };
    delete a._changed;
    return JSON.stringify(a);
};

const validateInput = (region) => {
    const errors = {};
    let canSave = true;
    if (!region.name || region.name.length <= 0 || region.name === '') {
        if (region._changed) {
            errors.name = { error: true, helperText: 'Region name is required' };
        }
        canSave = false;
    }
    return { canSave, errors };
};

const RegionEditForm = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { countries, countryMap, hasPerm } = React.useContext(ContextStore);
    const classes = useStyles();
    const [region, setRegion] = React.useState(props.data);
    const [originalState, setOriginalState] = React.useState(getComparableState(props.data));
    const country = region && region.country && region.country.length > 0 ? countryMap[region.country[0]] : null;
    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);
    const [currentTab, setCurrentTab] = React.useState(0);
    const { enqueueSnackbar } = useSnackbar();

    React.useEffect(() => {
        if (!props.data?.id) {
            return;
        }
        setRegion(props.data);
        setOriginalState(getComparableState(props.data));
    }, [props.data?.id]);

    const currentState = getComparableState(region);
    const hasUnsavedChanges = originalState !== currentState;
    const hasWritePerm = hasPerm('region', 'write');
    const [errors, setErrors] = React.useState({});
    const { canSave } = validateInput(region);

    const onRegionSave = (ev) => {
        props.setSyncActive && props.setSyncActive(true);
        const data = { ...region };
        delete data._changed;
        dataAPI(UPDATE, props.resource, { id: region.id, data: region, upload: region.upload })
            .then((res) => {
                if (res.statusCode < 400) {
                    props.onChange && props.onChange(region);
                    setOriginalState(getComparableState(region));
                    enqueueSnackbar('Region saved', { variant: 'success' });
                } else {
                    enqueueSnackbar('Error saving region', { variant: 'error' });
                }
            })
            .catch((err) => {
                enqueueSnackbar('Error saving region', { variant: 'error' });
            })
            .finally(() => {
                props.setSyncActive && props.setSyncActive(false);
                setRegion({ ...region, _changed: false });
            });
    };

    const onChange = (ev) => {
        setRegion({ ...region, [ev.target.name]: ev.target.value, _changed: true });
        props.setDetailsDirty && props.setDetailsDirty(true);
    };

    const onAccessTagsChange = (ev, atags, reason) => {
        setRegion((prevState) => ({ ...prevState, atag: atags, _changed: true }));
    };

    const onLicenseeChange = (id) => {
        setRegion((prev) => ({ ...prev, tour_operator: id, _changed: true }));
    };

    const doRegionDelete = (ev) => {
        props.onDelete && props.onDelete(region);
        props.onClose();
    };

    const onRegionDelete = (ev) => {
        setConfirmationDialogAction('delete_region');
    };

    const confirmationDialogCancel = () => {
        setConfirmationDialogAction(null);
    };

    const confirmationDialogConfirm = () => {
        console.log('confirm', confirmationDialogAction);
        switch (confirmationDialogAction) {
            case 'delete_region':
                doRegionDelete();
                break;
            default:
                break;
        }
        confirmationDialogCancel();
    };

    const handleSelectChange = (ev, country) => {
        country === null ? setRegion({ ...region, country: '', _changed: true, _changed_countries: true }) : setRegion({ ...region, country: [country.id], _changed: true, _changed_countries: true });
        props.setDetailsDirty && props.setDetailsDirty(true);
    };

    const tabPanelProps = (index) => ({
        className: index === 0 ? classes.tabPanelGeneral : classes.tabPanelHistory,
        value: currentTab,
        index,
    });

    const handleTabChange = (ev, newTab) => {
        setCurrentTab(newTab);
    }



    const handleOnBlur = (ev) => {
        setErrors(validateInput(region).errors);
    };



    return (
        <FormLicenseeContext.Provider value={toLicenseeId(region.tour_operator)}>
        <div className={classes.RegionEdit}>
            <PageMeta title={`Edit Region - ${region.name}`} />
            <Prompt when={hasUnsavedChanges && hasPerm('region', 'write')} message="Are you sure you want to leave without saving changes?" />
            {confirmationDialogAction ? (
                <ConfirmationDialog
                    classes={{ paper: classes.paper }}
                    keepMounted
                    onCancel={confirmationDialogCancel}
                    onConfirm={confirmationDialogConfirm}
                    title={ConfirmationDialogText[confirmationDialogAction].title}
                    okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                    cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                    open={!!confirmationDialogAction}
                >
                    {ConfirmationDialogText[confirmationDialogAction].body}
                </ConfirmationDialog>
            ) : null}
            <Grid container direction="row" spacing={2}>
                <Grid item xs={8}>
                    <Typography variant="h6">Region Details</Typography>
                </Grid>
                <Grid item xs={4} container justifyContent="flex-end" alignItems="center">
                    <IconButton aria-label="close-preview" onClick={props.onClose} size="small" sx={{ color: 'rgba(0, 0, 0, 0.87)' }}>
                        <CloseIcon />
                    </IconButton>
                </Grid>
            </Grid>
            <Tabs value={currentTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary">
                <Tab label="General" {...a11yProps(0)} />
                {hasPerm('tour_participant', 'view_history') && <Tab label="Change history" {...a11yProps(1)} />}
                {region.id && <Tab label="Used by" {...a11yProps(2)} />}
            </Tabs>
            <div className={classes.tabPanelsHost}>
            <TabPanel {...tabPanelProps(0)}>
                <Grid container direction="row" spacing={2}>
                    <Grid item xs={12}>
                        <LicenseeFormField value={region.tour_operator} onChange={onLicenseeChange} disabled={!hasWritePerm} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            disabled={!hasWritePerm}
                            helperText={`${28 - region.name.length} characters left`}
                            required
                            variant="filled"
                            size="small"
                            name="name"
                            label="Name"
                            fullWidth
                            value={region.name}
                            onChange={onChange}
                            inputProps={{ maxLength: 28 }}
                            onBlur={handleOnBlur}
                            FormHelperTextProps={{ sx: { mx: 0 } }}
                            {...errors.name}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl variant="filled" size="small" fullWidth className={classes.formControl} disabled={!hasWritePerm}>
                            <InputLabel htmlFor="card-arrangement-select-element">Card Arrangement</InputLabel>
                            <Select value={region.card_arrangement} onChange={onChange} inputProps={{ name: 'card_arrangement', id: 'card-arrangement-select-element' }}>
                                {cardArrangementTypesMenuItems}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl variant="filled" size="small" fullWidth className={classes.formControl}>
                            <Autocomplete
                                disabled={!hasWritePerm}
                                options={countries}
                                onChange={handleSelectChange}
                                noOptionsText={'No matches'}
                                value={country}
                                name="country"
                                getOptionLabel={(option) => option.name}
                                // renderOption={(option) => (<React.Fragment>{option.name}</React.Fragment>)}
                                renderInput={(params) => <TextField {...params} name="country" label="Choose Country" variant="filled" size="small" inputProps={{ ...params.inputProps, autoComplete: 'new-password' }} />}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            disabled={!hasWritePerm}
                            helperText={`${128 - (region.custom_id?.length || 0)} characters left`}
                            variant="filled"
                            size="small"
                            name="custom_id"
                            label="External ID"
                            fullWidth
                            value={region.custom_id}
                            onChange={onChange}
                            inputProps={{
                                maxLength: 128,
                            }}
                            FormHelperTextProps={{ sx: { mx: 0 } }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <AccessTagsDropdown disabled={!hasWritePerm} value={region.atag} onChange={onAccessTagsChange} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField disabled={!hasWritePerm} label="Latitude" name="latitude" value={region.latitude} onChange={onChange} variant="filled" size="small" fullWidth />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField disabled={!hasWritePerm} label="Longitude" name="longitude" value={region.longitude} onChange={onChange} variant="filled" size="small" fullWidth />
                    </Grid>
                </Grid>
            </TabPanel>
            <TabPanel {...tabPanelProps(1)}>
                <RevisionHistoryDialog open={currentTab === 1} resource={props.resource} id={region.id} />
            </TabPanel>
            {region.id && <TabPanel {...tabPanelProps(2)}>
                <UsedByPanel resource={props.resource} id={region.id} layout="narrow" />
            </TabPanel>}
            </div>
            {currentTab === 0 ? (
                <Box className={classes.formFooterDocked}>
                    <FormActionButtons
                        mode="edit"
                        size="medium"
                        onSubmit={onRegionSave}
                        submitDisabled={!hasWritePerm || props.syncActive || !canSave || !hasUnsavedChanges}
                        onCancel={props.onClose}
                        cancelDisabled={props.syncActive}
                        onDelete={onRegionDelete}
                        deleteDisabled={!hasPerm('region', 'delete') || props.syncActive}
                    />
                </Box>
            ) : null}
        </div>
        </FormLicenseeContext.Provider>
    );
};

const RegionEdit = (props) => {
    const resource = 'region';
    const routeParams = useParams();
    const id = routeParams.id;
    const { hasPerm } = React.useContext(ContextStore);

    if (!hasPerm(resource, 'read')) {
        return <AccessDeniedView />
    }
    return (
        <RecordDetails resource={resource} id={id} fullResponse>
            <RecordDetailsContext.Consumer>{(res) => <RegionEditForm {...props} resource={resource} id={id} data={res.data} />}</RecordDetailsContext.Consumer>
        </RecordDetails>
    );
};

export default RegionEdit;
